import { observer } from "mobx-react";
import { useRef, useState } from "react";
import className from "classnames";
import styles from "./styles/AccordionTerrace.module.scss";
import editStyles from "../../SchoolBlocks/styles/EditPane.module.scss";
import MarkdownEditor from "../../../editors/MarkdownEditor";
import WatsonApi from "../../../../backends/WatsonApi";
import { runInAction } from "mobx";
import NotificationManager from "../../../notifications/NotificationManager";
import { useStores } from "../../../../stores/useStores";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import SchoolBlocksLoadingIndicator from "../../../utilities/SchoolBlocksLoadingIndicator";
import FontAwesome from "../../../utilities/FontAwesome";
import { HtmlEditor, serializeSlateToHtml } from "../../../editors/HtmlEditor";
import type { SlateEditor } from "platejs";
import { NodeType } from "../../../editors/utils";
import { PlaceholderPlugin } from "@platejs/media/react";
import { compressImage } from "../../../../utils/DataUtilities";
import { getCdnUrl } from "../../../../utils/SchoolBlocksUtilities";
import { useMachine } from "@xstate/react";
import { FetchMachine } from "../../../machines/FetchMachine";
import { Box, Button } from "@radix-ui/themes";

const AccordionItemSummaryContentEditor = observer(
  (props: {
    title?: string;
    blockObj: IAccordionTerrace;
    index: number;
    handleSave: (item: IAccordionItemEditing) => Promise<IAccordionItem>;
    handleCancel: (accordionItem?: IAccordionItem) => void;
    item?: IAccordionItem;
    showContentEditor?: boolean;
    className?: string;
  }) => {
    const { organizationStore } = useStores();

    const [summary, setSummary] = useState<string | null>(
      props.item ? null : "",
    );
    const [content, setContent] = useState("");
    const [editSummary, setEditSummary] = useState(!props.showContentEditor);
    const [modalEnabled, setModalEnabled] = useState(
      props?.item?.modal_enabled,
    );
    const [current, send] = useMachine(FetchMachine);
    const htmlEditorRef = useRef<{ editor: SlateEditor }>(null);

    const handleSave = async () => {
      send("FETCH");
      const item: IAccordionItemEditing = {
        id: props?.item?.id,
        title: props.title,
        description: summary || "",
        modal_enabled: modalEnabled,
      };

      try {
        const client = await WatsonApi();
        let html;

        if (props.showContentEditor) {
          const editor = htmlEditorRef.current?.editor;
          if (!editor) return;

          const mediaPlaceholders = editor.api.nodes({
            match: { type: NodeType.MEDIA_PLACEHOLDER },
            at: [],
          });
          for (let placeholder of mediaPlaceholders) {
            const placeholderApi = editor.getApi(PlaceholderPlugin);
            const placeholderId = placeholder[0].id as string;
            const file =
              placeholderApi.placeholder.getUploadingFile(placeholderId);
            const compressedFile = await compressImage(file, 2);

            const response =
              await client.apis.organizations.organizations_terraces_upload_image(
                {
                  organization_pk: props.blockObj.organization_id,
                  id: props.blockObj.id,
                  file: compressedFile,
                },
              );
            const imageUrl = response.body.url;
            editor.tf.setNodes(
              {
                type: NodeType.IMAGE,
                url: getCdnUrl(imageUrl),
              },
              {
                at: placeholder[1],
              },
            );
          }

          html = await serializeSlateToHtml(editor);
          item.content_html = html;
        }

        const updatedItem = await props.handleSave(item);
        NotificationManager.success("Terrace successfully saved!");
        send("SUCCESS");
        props.handleCancel(updatedItem);
      } catch (e) {
        console.error(e);
        NotificationManager.error("Something went wrong! Please try again.");
        send("FAILURE");
      }
    };

    const titleIsValid = props.title ? props.title?.length > 0 : true;

    const markdownEditorClassName = className({
      [styles.markdownEditor]: true,
      [styles.markdownEditorContent]: true,
    });

    const handleSubmit = async () => {
      await handleSave();
    };

    const handleDelete = async () => {
      try {
        const client = await WatsonApi();
        await client.apis.organizations.organizations_terraces_accordion_items_delete(
          {
            organization_pk: props.blockObj.organization_id,
            terrace_pk: props.blockObj.id,
            id: (props.item as IAccordionItem).id,
          },
        );
        runInAction(() => {
          props.blockObj.items = (
            props.blockObj.items as IAccordionItem[]
          ).filter((item) => {
            return item.id !== (props.item as IAccordionItem).id;
          });
        });
        NotificationManager.success("Accordion Item deleted.");
      } catch (e) {
        NotificationManager.error(JSON.parse(e.response.data).message);
      }
    };

    const handleSummaryChange = (value) => {
      setSummary(value);
    };

    useAsyncEffect(async () => {
      if (props.item) {
        try {
          const client = await WatsonApi();
          const result =
            await client.apis.organizations.organizations_terraces_accordion_items_read(
              {
                organization_pk: organizationStore.currentOrganization.id,
                terrace_pk: (props.item as IAccordionItem).terrace_id,
                id: (props.item as IAccordionItem).id,
                expand: props.item.has_content ? ["content_html"] : [],
                raw: true,
              },
            );
          const accordionItem = JSON.parse(result.data);
          setSummary(accordionItem.description);
          setContent(accordionItem.content_html);
        } catch (e) {
          NotificationManager.error(e.response?.body?.detail);
        }
      }
    }, []);

    return (
      <div className={props.className}>
        {editSummary ? (
          <div className={editStyles.inputGroup}>
            <label>{"Summary (Optional)"}</label>
            {typeof summary === "string" && (
              <MarkdownEditor
                handleTextChange={handleSummaryChange}
                value={summary || ""}
                editorClassName={styles.markdownEditor}
              />
            )}
          </div>
        ) : (
          <div className={styles.editorButtons}>
            <Button onClick={() => setEditSummary(true)}>Edit Summary</Button>
          </div>
        )}
        {props.showContentEditor && (
          <div className={editStyles.inputGroup}>
            <label>
              Page Content{" "}
              <span
                className={styles.tooltip}
                data-content={
                  "Content will only be shown\nin the Accordion Item Modal\n" +
                  "and on the Accordion Item Page.\n" +
                  "Navigate to these locations respectively\n by clicking 'View More'\n" +
                  "on an Accordion Item with content, \n" +
                  "and by copying the page link from\n the 'Share' button in the modal."
                }
                style={{ marginLeft: ".25rem" }}
              >
                <FontAwesome prefix={"fas"} name={"fa-question-circle"} />
              </span>{" "}
              (Optional)
            </label>
            {props.item?.has_content && !content ? (
              <div>
                <SchoolBlocksLoadingIndicator />
              </div>
            ) : (
              <HtmlEditor
                value={content || ""}
                ref={htmlEditorRef}
                defaultEditor={"wysiwyg"}
                editorClassName={markdownEditorClassName}
              />
            )}
          </div>
        )}
        {props.showContentEditor || (
          <div className={styles.editorButtons}>
            <input
              type={"checkbox"}
              className={"form-control"}
              style={{
                display: "inline-flex",
                width: "1em",
                height: "auto",
              }}
              checked={modalEnabled}
              title={`Accordion Modal`}
              id={"Accordion Modal"}
              onChange={() => {
                setModalEnabled((v) => !v);
              }}
            />
            <label htmlFor={"Accordion Modal"}>Enable Modal</label>
          </div>
        )}
        <Box mb={"2"} className={styles.editorButtons}>
          <Button
            mr={"2"}
            disabled={!titleIsValid || current.value === "PENDING"}
            onClick={handleSubmit}
          >
            {current.value === "PENDING" ? (
              <>
                <FontAwesome prefix={"fas"} name={"fa-spinner"} spin />{" "}
                Saving...
              </>
            ) : (
              "Save Accordion Item"
            )}
          </Button>
          <Button
            mr={"2"}
            variant={"outline"}
            onClick={() => props.handleCancel()}
          >
            Cancel
          </Button>
          {props.item && (
            <Button variant={"outline"} onClick={handleDelete}>
              Delete
            </Button>
          )}
        </Box>
      </div>
    );
  },
);

export default AccordionItemSummaryContentEditor;
